/**
 * Compatibility layer for route migration
 * Helps handle both old (multi-document) and new (single-document) route architectures
 */

import { RouteStatus, RouteType } from './types'

/**
 * Check if a route ID is from the legacy multi-document system
 */
export function isLegacyRoute(routeId: string): boolean {
  return routeId.startsWith('autosave_') || 
         routeId.startsWith('draft_') || 
         routeId.startsWith('published_')
}

/**
 * Get the equivalent status from a legacy route ID
 */
export function getStatusFromLegacyId(routeId: string): RouteStatus {
  if (routeId.startsWith('autosave_')) return 'editing'
  if (routeId.startsWith('draft_')) return 'draft'
  if (routeId.startsWith('published_')) return 'published'
  
  // Default for new routes or unknown formats
  return 'editing'
}

/**
 * Get the equivalent status from the old type field
 */
export function getStatusFromType(type: RouteType): RouteStatus {
  switch (type) {
    case 'autosave': return 'editing'
    case 'draft': return 'draft'
    case 'published': return 'published'
    default: return 'editing'
  }
}

/**
 * Check if a route needs migration (has old type but no status)
 */
export function shouldMigrateRoute(route: any): boolean {
  return !route.status && route.type // Has old type but no status
}

/**
 * Get the appropriate route type for backward compatibility
 * @deprecated Only use this when you need to support old code
 */
export function getTypeFromStatus(status: RouteStatus): RouteType | undefined {
  switch (status) {
    case 'editing': return 'autosave'
    case 'draft': return 'draft' // 'draft' status maps to 'draft' type
    case 'submitted': return 'published' // Submitted routes are stored as published type
    case 'published': return 'published'
    default: return undefined
  }
}

/**
 * Determine if we should create a new route ID or reuse existing one
 */
export function shouldCreateNewRouteId(currentRouteId: string | null | undefined, targetStatus: RouteStatus): boolean {
  if (!currentRouteId) return true // No ID, must create new
  
  // In the new system, we always reuse the same ID
  if (!isLegacyRoute(currentRouteId)) return false
  
  // In the legacy system, we create new IDs when changing type
  const currentStatus = getStatusFromLegacyId(currentRouteId)
  
  // Legacy behavior: create new ID when moving between types
  if (currentStatus === 'editing' && targetStatus !== 'editing') return true
  if (currentStatus === 'draft' && targetStatus === 'published') return true
  
  return false
}